import { CookieService } from "ngx-cookie-service";
import { Observable } from "rxjs";
import { catchError, map } from "rxjs/operators";

import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";

import { Inspection, InspectionForOffline } from "../_objects/inspection";
import {
  InspectionImage,
  InspectionImageForOffline,
  InspectionImagesSortOrderForOffline,
  InspectionImageWithUploadUrl
} from "../_objects/inspection-image";
import { AnnotationImageDetail } from "../_objects/inspection-image-annotation";
import { IndividualReportForOffline, InspectionReport } from "../_objects/inspection-report";
import { ImageMetadataForOffline } from "../_objects/metadata/image-metadata-for-offline";
import { SettingsForOffline } from "../_objects/offline-management";
import { HttpService } from "./http.service";
import { MeasurementModeName } from "../_objects/measurement-mode-name";
import { ElectronService } from "../electron.service";

@Injectable({
  providedIn: "root"
})
export class OfflineManagementService extends HttpService {
  constructor(
    private http: HttpClient,
    private cookieService: CookieService,
    private router: Router,
    private electronService: ElectronService
  ) {
    super();
    super.setCookieService(cookieService);
    super.setHttpClient(http);
    super.setRouter(router);
  }

  /** 【OF01】 オフラインで使用するデータの一括取得
   *  GET /api/offline/settings
   * @returns Observable
   */
  getOfflineData(): Observable<SettingsForOffline> {
    const endpoint = super.getEndpoint("offline", "settings");

    return this.http
      .get<SettingsForOffline>(endpoint, { withCredentials: super.getWithCredentialsOption() })
      .pipe(map((response) => response as SettingsForOffline));
  }

  /** 【OF02】 オフラインで登録した検査のオンラインへ登録
   *  PUT /api/offline/inspections/{inspectionId}
   * @returns Observable
   */
  putOfflineInspection(inspectionId: string, inspection: InspectionForOffline): Observable<Inspection> {
    const endpoint = super.getEndpoint("offline", "inspections", inspectionId);

    return this.http
      .put<Inspection>(endpoint, inspection, { withCredentials: super.getWithCredentialsOption() })
      .pipe(map((response) => response as Inspection));
  }

  /** 【OF03】 オフラインで登録した検査画像情報をオンラインへ登録
   *  PUT /api/offline/inspections/{inspectionId}/images/{inspectionImageId}
   * @returns Observable
   */
  putOfflineInspectionImage(
    inspectionId: string,
    inspectionImageId: string,
    inspectionImage: InspectionImageForOffline
  ): Observable<InspectionImageWithUploadUrl> {
    const endpoint = super.getEndpoint("offline", "inspections", inspectionId, "images", inspectionImageId);

    return this.http
      .put<InspectionImageWithUploadUrl>(endpoint, inspectionImage, { withCredentials: super.getWithCredentialsOption() })
      .pipe(map((response) => response as InspectionImageWithUploadUrl));
  }

  /** 【OF04】 オフラインで登録した検査画像バイナリをオンラインへ登録
   *  PUT /api/offline/inspections/{inspectionId}/images/{inspectionImageId}/upload
   * @returns Observable
   */
  putOfflineInspectionImageBinary(
    inspectionId: string,
    inspectionImageId: string,
    file: File,
    fn: string,
    lid: string,
    path: string
  ): Observable<InspectionImage> {
    const endpoint = super.getEndpoint("offline", "inspections", inspectionId, "images", inspectionImageId, "upload");
    const formData = new FormData();
    formData.append("file", file, fn);

    let httpParams = new HttpParams();
    httpParams = httpParams.append("fn", fn);
    httpParams = httpParams.append("lid", lid);
    httpParams = httpParams.append("path", path);

    return this.http
      .put<InspectionImage>(endpoint, formData, { withCredentials: super.getWithCredentialsOption(), params: httpParams, reportProgress: true })
      .pipe(map((response) => response as InspectionImage));
  }

  /**
   * 【OF05】 オフラインで登録した編集済み検査画像バイナリをオンラインへ登録
   * PUT /api/offline/inspections/{inspectionId}/images/{inspectionImageId}/annotation/upload
   * @returns Observable
   */
  putOfflineInspectionModifiedImageBinary(
    inspectionId: string,
    inspectionImageId: string,
    file: File,
    usedImageMode: number
  ): Observable<AnnotationImageDetail> {
    const endpoint = super.getEndpoint("offline", "inspections", inspectionId, "images", inspectionImageId, "annotation", "upload");
    const formData = new FormData();
    formData.append("file", file, file.name);

    const httpParams: HttpParams = new HttpParams({ fromObject: { usedImageMode: usedImageMode } });

    return this.http
      .put<AnnotationImageDetail>(endpoint, formData, { withCredentials: super.getWithCredentialsOption(), reportProgress: true, params: httpParams })
      .pipe(map((response) => response as AnnotationImageDetail));
  }

  /** 【OF06】 オフラインで登録したレポートデータをオンラインへ登録
   *  PUT /api/offline/reports/{reportId}
   * @returns Observable
   */
  putOfflineIndividual(
    reportId: string,
    individualReport: IndividualReportForOffline,
    measurementModeName: MeasurementModeName
  ): Observable<InspectionReport> {
    const endpoint = super.getEndpoint("offline", "reports", reportId);

    let httpParams = new HttpParams();
    if (measurementModeName) {
      httpParams = httpParams
        .set("objectDistance", measurementModeName.objectDistance)
        .set("distance", measurementModeName.distance)
        .set("pointToLine", measurementModeName.pointToLine)
        .set("depth", measurementModeName.depth)
        .set("lines", measurementModeName.lines)
        .set("area", measurementModeName.area)
        .set("polyline", measurementModeName.polyline)
        .set("tipDefect", measurementModeName.tipDefect);
    }

    return this.http
      .put<InspectionReport>(endpoint, individualReport, { withCredentials: super.getWithCredentialsOption(), params: httpParams })
      .pipe(map((response) => response as InspectionReport));
  }

  /** 【OF09】オフラインで編集した検査画像順序をオンラインに登録
   *  PUT /api/offline/inspections/{inspectionId}/sortimages
   * @returns Observable
   */
  putOfflineInspectionImagesOrder(inspectionId: string, inspectionImagesSortOrder: InspectionImagesSortOrderForOffline): Observable<InspectionImage> {
    const endpoint = super.getEndpoint("offline", "inspections", inspectionId, "sortimages");

    return this.http
      .put<InspectionImage>(endpoint, inspectionImagesSortOrder, { withCredentials: super.getWithCredentialsOption() })
      .pipe(map((response) => response as InspectionImage));
  }

  /**
   * 【OF11】画像のMetadataをオフライン用にGetする
   * GET /api/offline/inspections/{inspectionId}/images/{inspectionImageId}/metadata
   *
   * @param inspectionId 検査ID。
   * @param inspectionImageId 検査画像ID。
   * @returns Observable<ImageMetadataForOffline>
   */
  public getInspectionImageMetadataForOffline(inspectionId: string, inspectionImageId: string): Observable<ImageMetadataForOffline> {
    const endpoint: string = super.getEndpoint("offline", "inspections", inspectionId, "images", inspectionImageId, "metadata");

    return this.http
      .get<ImageMetadataForOffline>(endpoint, { withCredentials: super.getWithCredentialsOption() })
      .pipe(map((response) => response as ImageMetadataForOffline));
  }
}
